// --------------------------------------------------------------------------
// NPS

// opaque bucket tokens, not display copy — consumers own their own labels
export const NPS_BUCKET_PROMOTERS = 'promoters'
export const NPS_BUCKET_PASSIVES = 'passives'
export const NPS_BUCKET_DETRACTORS = 'detractors'
const NPS_PROMOTER_MIN_RATING = 9
const NPS_PASSIVE_MIN_RATING = 7

/**
 * Classifies a raw 0-10 NPS answer into its standard bucket: promoters 9-10, passives 7-8, detractors 0-6.
 * @param {*} rating
 * @return {?string} one of the NPS_BUCKET_* tokens, or null when the rating is not a number
 */
export function getNPSBucket(rating) {
  if (typeof rating !== 'number' || isNaN(rating)) {
    return null
  }
  if (rating >= NPS_PROMOTER_MIN_RATING) {
    return NPS_BUCKET_PROMOTERS
  }
  if (rating >= NPS_PASSIVE_MIN_RATING) {
    return NPS_BUCKET_PASSIVES
  }
  return NPS_BUCKET_DETRACTORS
}

// --------------------------------------------------------------------------
// EXIT SURVEYS

/**
 * @typedef {Object} ExitSurveySubmissionAPIPayload
 * @property {number} building_group_id
 * @property {string} created_at
 * @property {number} nps_score
 * @property {Array<string>} reasons
 * @property {string} resident_name
 * @property {string} unit_number
 * @property {string} video_thumbnail_url
 * @property {string} video_url
 */

/**
 * @typedef ExitSurveyMetricDataPayload
 * @property {Object|string} settings
 * @property {{label: string, columns: Object, data: Array<Object>, date: string}} static
 * @property {Array<{building_group_id: ?number, building_group: string, submarket: string, property: string, data: Array<Object>}>} time_series
 */

/**
 * @typedef ExitSurveyNPS
 * @property {number} responses
 * @property {number} score
 * @property {number} scoreChange
 * @property {number} promoters
 * @property {number} promotersChange
 * @property {number} passives
 * @property {number} passivesChange
 * @property {number} detractors
 * @property {number} detractorsChange
 */

/**
 * @typedef ExitSurveySummary
 * @property {number} totalResponses
 * @property {number} totalResponsesChange
 * @property {?number} responseRate
 * @property {?number} responseRateChange
 */

/**
 * The requested asset's NPS score over time, in the same {data, columns} shape as Resident Sentiment's
 * TimeSeriesData — one plotted line, aligned index-for-index with `columns`.
 * @typedef ExitSurveyNPSTrend
 * @property {Array<string>} columns ISO dates, oldest first
 * @property {Array<Array<?number>>} data
 */

/**
 * @typedef ExitSurveyWindow
 * @property {ExitSurveyNPS} nps
 * @property {ExitSurveySummary} summary
 * @property {import('../../components/ExitSurveys/exitSurveyReasonData').ReasonBreakdown} reasonBreakdown
 */

/**
 * One resident's exit survey, as rendered by the Submission details list.
 * @typedef ExitSurveySubmission
 * @property {string} id
 * @property {string} residentName
 * @property {?string} unitNumber
 * @property {string} submittedAt
 * @property {number} npsRating out of 10, the raw answer rather than the -100..100 NPS score
 * @property {Array<import('../../components/ExitSurveys/exitSurveyVideoData').ReasonGroup>} reasonGroups
 * @property {?{url: string, thumbnailUrl: ?string, durationSeconds: ?number}} video null when none was recorded
 */

/**
 * @typedef ExitSurveyDashboardPayload
 * @property {ExitSurveyWindow} thirtyDay
 * @property {ExitSurveyWindow} sixMonth
 * @property {ExitSurveyNPSTrend} npsTrend
 * @property {Array<ExitSurveySubmission>} submissions
 * @property {Array<import('../../components/ExitSurveys/exitSurveyVideoData').VideoResponse>} videoResponses
 */

const EXIT_SURVEY_CATEGORY_PREFIX = 'category_'
const EXIT_SURVEY_REASON_PREFIX = 'reason_'
const EXIT_SURVEY_WINDOW_SUFFIX = {thirtyDay: '_30d', sixMonth: '_6m'}

/**
 * @param {Object|string|null|undefined} raw
 * @return {{causes: Object, categories: Object, reasons: Object}}
 */
function parseExitSurveySettings(raw) {
  let parsed = raw
  if (typeof raw === 'string') {
    // settings is AWSJSON; if it arrives malformed, only the reason labels are lost — degrade to an
    // empty taxonomy (empty reason chart) rather than failing the whole page into its error state
    try {
      parsed = JSON.parse(raw)
    } catch (err) {
      console.error(err)
      parsed = null
    }
  }

  return {
    causes: (parsed && parsed.causes) || {},
    categories: (parsed && parsed.categories) || {},
    reasons: (parsed && parsed.reasons) || {},
  }
}

/**
 * @param {Object} row a `static.data` row, keyed by metric name
 * @param {string} metric
 * @return {number} 0 rather than NaN, so absent metrics simply do not contribute to a total
 */
function metricCount(row, metric) {
  const value = row[metric]
  return typeof value === 'number' && !isNaN(value) ? value : 0
}

/**
 * @param {Object} row
 * @param {string} metric
 * @return {number} NaN when absent, which is what the NPS card renders as "N/A"
 */
function metricValue(row, metric) {
  const value = row[metric]
  return typeof value === 'number' ? value : NaN
}

/**
 * @param {Object} row
 * @param {{categories: Object, reasons: Object}} settings
 * @param {string} suffix
 * @param {number} totalSubmissions
 * @return {import('../../components/ExitSurveys/exitSurveyReasonData').ReasonBreakdown}
 */
function constructExitSurveyReasonBreakdown(row, settings, suffix, totalSubmissions) {
  const categories = Object.keys(settings.categories)
    .map(categoryId => {
      const subReasons = Object.keys(settings.reasons)
        .filter(reasonId => settings.reasons[reasonId].category === categoryId)
        .map(reasonId => ({
          id: reasonId,
          label: settings.reasons[reasonId].label,
          count: metricCount(row, `${EXIT_SURVEY_REASON_PREFIX}${reasonId}${suffix}`),
        }))
        .filter(subReason => subReason.count > 0)
        .sort((a, b) => b.count - a.count)

      // the category rollup metric counts the same selections its reasons do, so prefer it and fall
      // back to summing the reasons only when the API has not sent it
      const rollup = metricCount(row, `${EXIT_SURVEY_CATEGORY_PREFIX}${categoryId}${suffix}`)
      const count = rollup || subReasons.reduce((sum, subReason) => sum + subReason.count, 0)

      return {
        id: categoryId,
        label: settings.categories[categoryId].label,
        count,
        // there is no distinct residents-per-category metric, so the sub-chart's "submissions selected
        // this category" note shows the selection count — never 0 while the bars clearly have data
        submissions: count,
        subReasons,
      }
    })
    .filter(category => category.count > 0)
    .sort((a, b) => b.count - a.count)

  return {totalSubmissions, categories}
}

/**
 * @param {Object} row
 * @param {{categories: Object, reasons: Object}} settings
 * @param {string} windowKey `thirtyDay` or `sixMonth`
 * @return {ExitSurveyWindow}
 */
function constructExitSurveyWindow(row, settings, windowKey) {
  const suffix = EXIT_SURVEY_WINDOW_SUFFIX[windowKey]
  const responses = metricValue(row, `exit_survey_count${suffix}`)
  const totalSubmissions = metricCount(row, `exit_survey_count${suffix}`)

  return {
    nps: {
      responses,
      score: metricValue(row, `exit_nps_score_avg${suffix}`),
      scoreChange: metricValue(row, `exit_nps_score_avg${suffix}_diff${suffix}`),
      promoters: metricValue(row, `exit_nps_promoter_percentage${suffix}`),
      promotersChange: metricValue(row, `exit_nps_promoter_percentage${suffix}_diff${suffix}`),
      passives: metricValue(row, `exit_nps_passive_percentage${suffix}`),
      passivesChange: metricValue(row, `exit_nps_passive_percentage${suffix}_diff${suffix}`),
      detractors: metricValue(row, `exit_nps_detractor_percentage${suffix}`),
      detractorsChange: metricValue(row, `exit_nps_detractor_percentage${suffix}_diff${suffix}`),
    },
    summary: {
      totalResponses: responses,
      totalResponsesChange: metricValue(row, `exit_survey_count${suffix}_diff${suffix}`),
      // likewise no response-rate metric; the card falls back to its empty state
      responseRate: undefined,
      responseRateChange: undefined,
    },
    reasonBreakdown: constructExitSurveyReasonBreakdown(row, settings, suffix, totalSubmissions),
  }
}

/**
 * The dashboard shows a single asset at a time, so only that asset's series is kept even though
 * `time_series` carries every building group plus the portfolio rollup. The requested asset is taken as
 * the first entry with a `building_group_id` (the portfolio row has none), falling back to the rollup
 * only when no asset rows exist at all.
 * @param {Array<Object>|{data: Array<Object>}} timeSeries
 * @return {ExitSurveyNPSTrend}
 */
function constructExitSurveyNPSTrend(timeSeries) {
  // The API wraps the groups in `time_series.data`, and the points for each
  // group in `graph` (older responses used `data` for the points).
  const groups = (Array.isArray(timeSeries) ? timeSeries : timeSeries?.data || []).filter(
    group => (Array.isArray(group.graph) && group.graph.length > 0) || (Array.isArray(group.data) && group.data.length > 0),
  )
  const asset = groups.find(group => group.building_group_id) || groups[0]
  if (!asset) {
    return {columns: [], data: []}
  }

  const points = Array.isArray(asset.graph) ? asset.graph : asset.data

  return {
    columns: points.map(point => point.dt),
    data: [points.map(point => (typeof point.exit_nps_score_avg_30d === 'number' ? point.exit_nps_score_avg_30d : null))],
  }
}

/**
 * @param {{settings: *, static: {data: Array<Object>}, time_series: Array<Object>}} statsResponse
 * @param {{submissions: Array<ExitSurveySubmissionAPIPayload>}} submissionsResponse
 * @return {ExitSurveyDashboardPayload}
 */
export function constructExitSurveyDashboardPayload(statsResponse, submissionsResponse) {
  const settings = parseExitSurveySettings(statsResponse && statsResponse.settings)
  const row = (statsResponse && statsResponse.static && statsResponse.static.data && statsResponse.static.data[0]) || {}

  return {
    thirtyDay: constructExitSurveyWindow(row, settings, 'thirtyDay'),
    sixMonth: constructExitSurveyWindow(row, settings, 'sixMonth'),
    npsTrend: constructExitSurveyNPSTrend(statsResponse && statsResponse.time_series),
    submissions: (submissionsResponse?.submissions || []).map(submissionPayloadToExitSurveySubmission),
    videoResponses: [],
  }
}

/**
 * @param {ExitSurveySubmissionAPIPayload} payload
 * @returns {ExitSurveySubmission}
 */
function submissionPayloadToExitSurveySubmission(payload) {
  return {
    id: payload.id,
    residentName: payload.resident_name,
    unitNumber: payload.unit_number || null,
    submittedAt: payload.created_at,
    npsRating: payload.nps_score,
    reasonGroups: [], // TODO: parse the reasons into the grouped structure
    video: payload.video_url
      ? {
          url: payload.video_url,
          thumbnailUrl: payload.video_thumbnail_url || null,
          durationSeconds: null, // TODO: if the API returns this, populate it
        }
      : null,
  }
}

/**
 * @typedef CommandCenterMetricDataPayload
 * @property {number} sentiment
 * @property {number} sentimentDelta
 * @property {number} sentimentRevenue
 * @property {number} engagement
 * @property {number} engagementDelta
 * @property {number} engagementRevenue
 * @property {number} guests
 * @property {number} guestsDelta
 * @property {number} guestsRevenue
 * @property {number} incomingDeliveries
 * @property {number} incomingDeliveriesDelta
 * @property {number} incomingDeliveriesRevenue
 * @property {number} outgoingDeliveries
 * @property {number} outgoingDeliveriesDelta
 * @property {number} outgoingDeliveriesRevenue
 * @property {number} cleanings
 * @property {number} cleaningsDelta
 * @property {number} cleaningsRevenue
 * @property {number} amenities
 * @property {number} amenitiesDelta
 * @property {number} amenitiesRevenue
 * @property {number} maintenance
 * @property {number} maintenanceDelta
 * @property {number} maintenanceRevenue
 * @property {number} dogWalking
 * @property {number} dogWalkingDelta
 * @property {number} dogWalkingRevenue
 * @property {number} massage
 * @property {number} massageDelta
 * @property {number} massageRevenue
 * @property {number} personalTraining
 * @property {number} personalTrainingDelta
 * @property {number} personalTrainingRevenue
 * @property {number} events
 * @property {number} eventsDelta
 * @property {number} eventsRevenue
 */

/**
 * @typedef {{data: Array<{score: number, change: number, totalAmount: number}>}} CommandCenterMetricResponse
 */

/**
 * @param {CommandCenterMetricResponse} response
 * @param {string} prop
 * @returns {*}
 */
function getScoreAndDeltaFromResponse(response, prop) {
  const valueProp = prop
  const deltaProp = `${prop}Delta`
  const revenueProp = `${prop}Revenue`

  if (!response || !response.data || !response.data[0]) {
    this[valueProp] = this[deltaProp] = undefined
  } else {
    this[valueProp] = response.data[0].score
    this[deltaProp] = response.data[0].change
    this[revenueProp] = response.data[0].totalAmount
  }

  return this
}

/**
 * --- 14 total metrics
 * @param {Object<string, CommandCenterMetricResponse>} res
 * @returns {CommandCenterMetricDataPayload}
 */
export function constructCommandCenterMetricDataPayloadFromResponses(res) {
  // the props we want from the response payload
  const propNames = [
    'sentiment',
    // 'engagement',
    'guests',
    'incomingDeliveries',
    'outgoingDeliveries',
    'cleanings',
    'amenities',
    'maintenance',
    'dogWalking',
    'massage',
    'personalTraining',
    'events',
  ]

  return propNames.reduce((acc, prop) => {
    getScoreAndDeltaFromResponse.call(acc, res[prop], prop)
    return acc
  }, {})
}

// --------------------------------------------------------------------------
// RESIDENT SENTIMENT
/**
 * @typedef FeedbackDetailsPayload
 * @property {number} total
 * @property {number} average
 * @property {Array<{label: string, value: number, createdAt: Date, comments: string, denominator: number}>} items
 */

/**
 * @param {*} response
 * @returns {FeedbackDetailsPayload}
 */
export function constructFeedbackDetailsPayloadFromResponse(response) {
  return {
    total: response.total,
    average: response.average,
    items: response.items,
  }
}

/**
 * @typedef TimeSeriesData
 * @property {Array<Array<number>>} data
 * @property {Array<string>} columns
 */

/**
 * @typedef ResidentFeedbackSummaryAndTimeSeriesPayload
 * @property {TimeSeriesData} nps
 * @property {TimeSeriesData} amenities
 * @property {TimeSeriesData} sentiment
 * @property {TimeSeriesData} services
 * @property {TimeSeriesData} maintenance
 * @property {{
 *   npsResponses: number,
 *   npsScore: number,
 *   npsScoreChange: number,
 *   npsScorePercentagePromoters: number,
 *   npsScorePercentagePromotersChangeFromLastMonth: number,
 *   npsScorePercentagePassives: number,
 *   npsScorePercentagePassivesChangeFromLastMonth: number,
 *   npsScorePercentageDetractors: number,
 *   npsScorePercentageDetractorsChangeFromLastMonth: number,
 *   messageSentiment: number,
 *   messageSentimentChangeFromLastMonth: number,
 *   serviceRating: number,
 *   serviceRatingChangeFromLastMonth: number,
 *   amenitiesRating: number,
 *   amenitiesRatingChangeFromLastMonth: number,
 *   maintenanceRating: number,
 *   maintenanceRatingChangeFromLastMonth: number,
 * }} summary
 */

/**
 * @param {*} response
 * @returns {ResidentFeedbackSummaryAndTimeSeriesPayload}
 */
export function constructResidentFeedbackSummaryAndTimeSeriesPayload(response) {
  return {
    sentiment: convertTimeSeriesApiPayloadToFrontEndLineGraphStructure(response?.time_series?.sentiment, 'sentiment_avg_30d_cur'),
    nps: convertTimeSeriesApiPayloadToFrontEndLineGraphStructure(response?.time_series?.nps_score, 'nps_score_avg_30d'),
    amenities: convertTimeSeriesApiPayloadToFrontEndLineGraphStructure(response?.time_series?.amenity_rating, 'amenities_rating_avg_30d'),
    services: convertTimeSeriesApiPayloadToFrontEndLineGraphStructure(response?.time_series?.service_rating, 'service_rating_avg_30d'),
    maintenance: convertTimeSeriesApiPayloadToFrontEndLineGraphStructure(
      response?.time_series?.maintenance_rating,
      'maintenance_rating_avg_30d',
    ),
    onboarding: convertTimeSeriesApiPayloadToFrontEndLineGraphStructure(
      response?.time_series?.onboarding_rating,
      'onboarding_rating_avg_30d',
    ),
    summary: {
      npsResponses: response?.static?.data?.[0].nps_responses_sum_30d,
      npsScore: response?.static?.data?.[0].nps_score_avg_30d,
      npsScoreChange: response?.static?.data?.[0].nps_score_avg_30d_change_30d,
      npsScorePercentagePromoters: response?.static?.data?.[0].nps_percentage_promoters_avg_30d,
      npsScorePercentagePromotersChangeFromLastMonth: response?.static?.data?.[0].nps_percentage_promoters_avg_30_change_30d, // sic, the metric name actually has a typo (missing D in 30d) right now.
      npsScorePercentagePassives: response?.static?.data?.[0].nps_percentage_passives_avg_30d,
      npsScorePercentagePassivesChangeFromLastMonth: response?.static?.data?.[0].nps_percentage_passives_avg_30d_change_30d,
      npsScorePercentageDetractors: response?.static?.data?.[0].nps_percentage_detractors_avg_30d,
      npsScorePercentageDetractorsChangeFromLastMonth: response?.static?.data?.[0].nps_percentage_detractors_avg_30d_change_30d,
      messageSentiment: response?.static?.data?.[0].sentiment_avg_30d_cur,
      messageSentimentChangeFromLastMonth: response?.static?.data?.[0].sentiment_avg_30d_dif, // TODO: not sure this is the right metric?
      serviceRating: response?.static?.data?.[0].service_rating_avg_30d,
      serviceRatingChangeFromLastMonth: response?.static?.data?.[0].service_rating_avg_30d_change_30d,
      amenitiesRating: response?.static?.data?.[0].amenities_rating_avg_30d,
      amenitiesRatingChangeFromLastMonth: response?.static?.data?.[0].amenities_rating_avg_30d_change_30d,
      maintenanceRating: response?.static?.data?.[0].service_rating_avg_30d,
      maintenanceRatingChangeFromLastMonth: response?.static?.data?.[0].service_rating_avg_30d_change_30d,
    },
  }
}

function convertTimeSeriesApiPayloadToFrontEndLineGraphStructure(payload, metricOfInterest) {
  if (!payload || !payload.data || payload.data.length === 0) {
    return {data: [], columns: []}
  }

  const data = []
  payload.data.forEach(line => {
    const newDataEnty = []
    line.graph.forEach(dataPoint => {
      newDataEnty.push(dataPoint[metricOfInterest])
    })
    data.push(newDataEnty)
  })

  const columns = payload.data[0].graph.map(dataPoint => dataPoint.dt)

  return {data, columns}
}
